/**
 * Transformations sur quadrillage — logique pure (Boîte à outils, 6e → 3e).
 *
 * Programme de cycle 4 (BO du 5 mars 2026) et de 6e (BO 2025) : symétrie
 * axiale en 6e, demi-tour (symétrie centrale) en 5e, translation en 4e (son
 * effet, sans définition ponctuelle), translation et vecteurs en 3e
 * (définition par le parallélogramme). Pas de rotation ni d'homothétie.
 * Tous les points sont sur le quadrillage : axes verticaux, horizontaux ou
 * diagonaux passant par des nœuds, centre au demi-nœud, vecteur entier.
 */

export interface Pt {
  x: number;
  y: number;
}

export type Kind = 'axial' | 'central' | 'translation';

/** x = c ; y = c ; y = x + c ; y = −x + c */
export type AxisDir = 'v' | 'h' | 'd1' | 'd2';

export interface Axis {
  dir: AxisDir;
  c: number;
}

export interface Params {
  axis: Axis;
  center: Pt;
  /** vecteur de la translation : de `from` vers `to` */
  from: Pt;
  to: Pt;
}

export const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'] as const;

export const PRESETS: ReadonlyArray<{ id: string; name: string; pts: Pt[] }> = [
  {
    id: 'drapeau',
    name: 'Drapeau',
    pts: [
      { x: -4, y: -2 },
      { x: -4, y: 2 },
      { x: -1, y: 2 },
      { x: -2, y: 1 },
      { x: -1, y: 0 },
    ],
  },
  {
    id: 'triangle',
    name: 'Triangle',
    pts: [
      { x: -5, y: -1 },
      { x: -2, y: -2 },
      { x: -3, y: 3 },
    ],
  },
  {
    id: 'equerre',
    name: 'Équerre',
    pts: [
      { x: -5, y: -3 },
      { x: -2, y: -3 },
      { x: -2, y: -2 },
      { x: -4, y: -2 },
      { x: -4, y: 1 },
      { x: -5, y: 1 },
    ],
  },
];

const clean = (v: number) => (Object.is(v, -0) ? 0 : Number(v.toFixed(9)));
const pt = (x: number, y: number): Pt => ({ x: clean(x), y: clean(y) });

export function reflect(p: Pt, a: Axis): Pt {
  switch (a.dir) {
    case 'v':
      return pt(2 * a.c - p.x, p.y);
    case 'h':
      return pt(p.x, 2 * a.c - p.y);
    case 'd1':
      return pt(p.y - a.c, p.x + a.c);
    case 'd2':
      return pt(a.c - p.y, a.c - p.x);
  }
}

export const halfTurn = (p: Pt, o: Pt): Pt => pt(2 * o.x - p.x, 2 * o.y - p.y);

export const translate = (p: Pt, from: Pt, to: Pt): Pt =>
  pt(p.x + to.x - from.x, p.y + to.y - from.y);

export function imageOf(kind: Kind, p: Pt, q: Params): Pt {
  if (kind === 'axial') return reflect(p, q.axis);
  if (kind === 'central') return halfTurn(p, q.center);
  return translate(p, q.from, q.to);
}

/** pied de la perpendiculaire à l'axe passant par p (milieu de [PP']) */
export function footOnAxis(p: Pt, a: Axis): Pt {
  const r = reflect(p, a);
  return pt((p.x + r.x) / 2, (p.y + r.y) / 2);
}

/** côté de l'axe : signe (0 sur l'axe) */
export function sideOf(p: Pt, a: Axis): number {
  switch (a.dir) {
    case 'v':
      return Math.sign(p.x - a.c);
    case 'h':
      return Math.sign(p.y - a.c);
    case 'd1':
      return Math.sign(p.y - p.x - a.c);
    case 'd2':
      return Math.sign(p.y + p.x - a.c);
  }
}

/** les deux bouts de l'axe dans la fenêtre carrée [−m ; m]² */
export function axisSegment(a: Axis, m: number): [Pt, Pt] {
  switch (a.dir) {
    case 'v':
      return [pt(a.c, -m), pt(a.c, m)];
    case 'h':
      return [pt(-m, a.c), pt(m, a.c)];
    case 'd1':
      return [pt(-m, -m + a.c), pt(m, m + a.c)];
    case 'd2':
      return [pt(-m, m + a.c), pt(m, -m + a.c)];
  }
}

/** un point de l'axe sur la grille, près de p : la poignée de l'axe */
export function axisHandle(a: Axis): Pt {
  switch (a.dir) {
    case 'v':
      return pt(a.c, 0);
    case 'h':
      return pt(0, a.c);
    case 'd1':
      return pt(0, a.c);
    case 'd2':
      return pt(0, a.c);
  }
}

/** l'axe qui passe par le nœud p, dans la même direction */
export function axisThrough(dir: AxisDir, p: Pt): Axis {
  switch (dir) {
    case 'v':
      return { dir, c: p.x };
    case 'h':
      return { dir, c: p.y };
    case 'd1':
      return { dir, c: p.y - p.x };
    case 'd2':
      return { dir, c: p.y + p.x };
  }
}

export const samePt = (p: Pt, q: Pt) => Math.abs(p.x - q.x) < 1e-9 && Math.abs(p.y - q.y) < 1e-9;
const fmt = (v: number) => String(clean(v)).replace('.', ',').replace('-', '−');
export const ptText = (p: Pt) => `(${fmt(p.x)} ; ${fmt(p.y)})`;

/**
 * Mode Construire : que dire d'un point placé pour l'image de P ? null si
 * c'est juste, sinon une phrase qui pointe l'erreur sans donner la réponse.
 */
export function feedback(kind: Kind, p: Pt, guess: Pt, q: Params, name: string): string | null {
  const want = imageOf(kind, p, q);
  if (samePt(guess, want)) return null;
  if (samePt(guess, p)) return `C’est ${name} lui-même : son image est ailleurs.`;
  if (kind === 'axial') {
    const a = q.axis;
    if (sideOf(p, a) !== 0 && sideOf(guess, a) === sideOf(p, a))
      return `${name}′ doit être de l’autre côté de l’axe.`;
    // sur la perpendiculaire à l'axe passant par P ?
    const dx = guess.x - p.x;
    const dy = guess.y - p.y;
    const onPerp =
      a.dir === 'v' ? dy === 0 : a.dir === 'h' ? dx === 0 : a.dir === 'd1' ? dx === -dy : dx === dy;
    if (!onPerp)
      return `${name}′ est sur la perpendiculaire à l’axe qui passe par ${name}, à la même distance de l’axe.`;
    return a.dir === 'v' || a.dir === 'h'
      ? `Même distance à l’axe des deux côtés : compte les carreaux de ${name} jusqu’à l’axe.`
      : `Même distance à l’axe des deux côtés : compte les diagonales de carreaux de ${name} jusqu’à l’axe.`;
  }
  if (kind === 'central')
    return `${name}′ est sur la droite (${name}O), de l’autre côté de O, à la même distance de O : O est le milieu de [${name}${name}′].`;
  // translation : dire ce qui cloche sans donner la réponse
  const dx = q.to.x - q.from.x;
  const dy = q.to.y - q.from.y;
  const gx = guess.x - p.x;
  const gy = guess.y - p.y;
  const parts: string[] = [];
  if (gx !== dx) parts.push('vers la droite ou la gauche');
  if (gy !== dy) parts.push('vers le haut ou le bas');
  return `On fait glisser ${name} comme la flèche : même direction, même sens, même longueur. Recompte de combien de carreaux la flèche avance ${parts.join(', puis ')}.`;
}

/** ce que la transformation conserve, au niveau de l'élève */
export function properties(kind: Kind, rank: number): string {
  if (kind === 'axial')
    return 'La symétrie axiale conserve les longueurs, les angles et les aires. La figure est retournée, comme dans un miroir : l’ordre des sommets change de sens.';
  if (kind === 'central')
    return 'Le demi-tour conserve l’alignement, le parallélisme, les longueurs et les angles. La figure tourne d’un demi-tour autour de O sans être retournée.';
  return rank >= 4
    ? 'La translation qui transforme A en A′ transforme B en B′ tel que ABB′A′ est un parallélogramme (éventuellement aplati) : c’est la translation de vecteur AA′. Elle conserve longueurs, angles, parallélisme et aires.'
    : 'La translation fait glisser la figure sans la tourner ni la retourner : longueurs, angles, parallélisme et aires sont conservés. Observe : A, B, B′ et A′ forment un parallélogramme.';
}

/** sens de parcours des sommets (aire signée) : la symétrie axiale l'inverse */
export function orientation(pts: readonly Pt[]): number {
  let s = 0;
  pts.forEach((p, i) => {
    const q = pts[(i + 1) % pts.length]!;
    s += p.x * q.y - q.x * p.y;
  });
  return Math.sign(s);
}

export function area(pts: readonly Pt[]): number {
  let s = 0;
  pts.forEach((p, i) => {
    const q = pts[(i + 1) % pts.length]!;
    s += p.x * q.y - q.x * p.y;
  });
  return Math.abs(s) / 2;
}

/** deux segments [ab] et [cd] se coupent-ils (en dehors d'une extrémité commune) ? */
function crosses(a: Pt, b: Pt, c: Pt, d: Pt): boolean {
  const o = (p: Pt, q: Pt, r: Pt) =>
    Math.sign((q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x));
  const o1 = o(a, b, c);
  const o2 = o(a, b, d);
  const o3 = o(c, d, a);
  const o4 = o(c, d, b);
  if (o1 !== o2 && o3 !== o4 && o1 * o2 <= 0 && o3 * o4 <= 0) {
    // contact en un sommet commun : pas un croisement
    return !(samePt(a, c) || samePt(a, d) || samePt(b, c) || samePt(b, d));
  }
  return false;
}

/** polygone simple : aucun côté n'en coupe un autre non voisin */
export function isSimple(pts: readonly Pt[]): boolean {
  const n = pts.length;
  for (let i = 0; i < n; i++)
    for (let j = i + 1; j < n; j++) {
      if (j === i + 1 || (i === 0 && j === n - 1)) continue;
      if (crosses(pts[i]!, pts[(i + 1) % n]!, pts[j]!, pts[(j + 1) % n]!)) return false;
    }
  return true;
}
